import { isPromise } from '../../utility/helper';
import windowService from '../window-service';
import AnimationsAction from './animation-actions';

export type AvailableAnimations = keyof typeof AnimationsAction;
type TimingFunction = keyof typeof timingFunctions;
export type AnimationGeneratorType = {
  cleanUpAfterAnimation?: ((el: HTMLElement) => Promise<void>) | ((el: HTMLElement) => void) | boolean;
  duration?: number;
};
export type EffectType = {
  name: AvailableAnimations;
  to?: number;
  from?: number;
  timingFunction?: TimingFunction;
};

const resetStyle = (el: HTMLElement): void => {
  el.removeAttribute('style');
};

export const getAnimationEffect = (
  effectName: AvailableAnimations
): ((el: HTMLElement, progress: number, effect: EffectType) => void) => {
  return AnimationsAction[effectName];
};

/* istanbul ignore next */
const timingFunctions = {
  easeInOutSin: (timeFraction: number): number => {
    return -(Math.cos(Math.PI * timeFraction) - 1) / 2;
  },
  easeInOutQuad: (timeFraction: number): number => {
    if (timeFraction < 0.5) {
      return (2 * timeFraction) / 2;
    } else {
      return (2 - 2 * (1 - timeFraction)) / 2;
    }
  },
  linear: (timeFraction: number): number => {
    return timeFraction < 0 || timeFraction > 1 ? Math.round(timeFraction) : timeFraction;
  },
  cubicBezierStart: (timeFraction: number) => {
    return cubicBezier(timeFraction, 0.1, 0.0, 0.0, 100);
  },
  cubicBezierEnd: (timeFraction: number) => {
    return cubicBezier(timeFraction, 0.3, 0.0, 1.0, 0.8);
  }
} as const;

export function animationPromiseGenerator(
  el: HTMLElement,
  animation: AnimationGeneratorType & EffectType
): Promise<void> {
  const { duration = 300, cleanUpAfterAnimation = false, ...effect } = animation;

  return new Promise<void>((resolve) => {
    let animationFrame: number;

    const timeout = setTimeout(() => {
      cancelAnimationFrame(animationFrame);
      finishAnimation();
    }, duration + 50);

    function finishAnimation() {
      playEffect(el, 1, effect);

      clearTimeout(timeout);
      if (typeof cleanUpAfterAnimation === 'function') {
        const maybePromise: void | Promise<void> = cleanUpAfterAnimation(el);
        if (isPromise<void>(maybePromise)) {
          maybePromise.then(() => resolve());
          return;
        }
      } else if (cleanUpAfterAnimation === true) {
        resetStyle(el);
      }

      resolve();
    }

    const start = performance.now();
    const playEffect = getAnimationEffect(effect.name);
    function animate(time: DOMHighResTimeStamp) {
      const timingFunction = timingFunctions[effect.timingFunction || 'linear'];
      let progress = timingFunction((time - start) / duration);
      progress = Math.round((progress + Number.EPSILON) * 1000) / 1000;

      const ANIMATION_HAS_FINISHED = progress >= 1;
      const ANIMATION_ON_PROGRESS = progress < 1;

      if (ANIMATION_HAS_FINISHED) {
        return finishAnimation();
      }

      playEffect(el, progress, effect);

      if (ANIMATION_ON_PROGRESS) {
        animationFrame = windowService.requestAnimationFrame(animate);
      }
    }

    animationFrame = windowService.requestAnimationFrame(animate);
  });
}

function cubicBezier(t: number, initial: number, p1: number, p2: number, final: number) {
  return (
    (1 - t) * (1 - t) * (1 - t) * initial +
    3 * (1 - t) * (1 - t) * t * p1 +
    3 * (1 - t) * t * t * p2 +
    t * t * t * final
  );
}
